// ============================================
// FILE: src/components/fashion/FashionFilterSheet.jsx
// ============================================
//
// Desktop: a persistent left sidebar (pass `inline`) that applies on every
// click — no Apply button, which is what a marketplace grid expects.
// Mobile: a bottom sheet with a left rail of groups and a right pane of values.
//
// The price range is two stacked native <input type="range"> elements rather
// than a slider dependency — pointer-events is off on the track and on for the
// thumbs so both stay grabbable.

import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

const EMPTY = { color: [], size: [], brand: [], min_price: null, max_price: null, in_stock: false };

const toggleIn = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const FashionFilterSheet = ({
  isOpen = false,
  onClose = () => {},
  isDesktop = false,
  inline = false,
  facets = null,
  value = EMPTY,
  onApply = () => {},
  onClear = () => {},
}) => {
  const [draft, setDraft] = useState(value);
  const [activeGroup, setActiveGroup] = useState(null);

  const priceBounds = useMemo(() => ({
    min: Math.floor(facets?.price?.min ?? 0),
    max: Math.ceil(facets?.price?.max ?? 0),
  }), [facets]);

  useEffect(() => {
    if (isOpen || inline) setDraft(value);
  }, [isOpen, inline, value]);

  const groups = useMemo(() => {
    const list = (facets?.options || []).map((option) => ({
      key: option.code,
      label: option.label,
      type: option.type,
      values: option.values,
    }));

    if (facets?.brands?.length) {
      list.push({
        key: 'brand',
        label: 'Brand',
        type: 'checkbox',
        values: facets.brands.map((b) => ({ value: b.value, label: b.value, count: b.count })),
      });
    }

    if (priceBounds.max > priceBounds.min) {
      list.push({ key: 'price', label: 'Price', type: 'range', values: [] });
    }

    return list;
  }, [facets, priceBounds]);

  useEffect(() => {
    if (!activeGroup && groups.length > 0) setActiveGroup(groups[0].key);
  }, [groups, activeGroup]);

  if (!inline && !isOpen) return null;

  const activeCount =
    draft.color.length + draft.size.length + draft.brand.length +
    (draft.min_price != null || draft.max_price != null ? 1 : 0) +
    (draft.in_stock ? 1 : 0);

  // Desktop applies immediately; mobile batches until Apply is pressed.
  const commit = (next) => {
    setDraft(next);
    if (inline) onApply(next);
  };

  const setListValue = (key, v) =>
    commit({ ...draft, [key]: toggleIn(draft[key] || [], v) });

  const renderValues = (group) => {
    if (group.type === 'range') {
      const min = draft.min_price ?? priceBounds.min;
      const max = draft.max_price ?? priceBounds.max;
      const span = priceBounds.max - priceBounds.min || 1;

      return (
        <div className="pt-2 pb-1">
          <div className="relative h-6" style={{ pointerEvents: 'none' }}>
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0" style={{ height: 3, background: 'var(--fx-line)' }} />
            <div
              className="absolute top-1/2 -translate-y-1/2"
              style={{
                height: 3,
                background: 'var(--fx-pink)',
                left: `${((min - priceBounds.min) / span) * 100}%`,
                right: `${100 - ((max - priceBounds.min) / span) * 100}%`,
              }}
            />
            <input
              type="range"
              min={priceBounds.min}
              max={priceBounds.max}
              value={min}
              onChange={(e) => commit({ ...draft, min_price: Math.min(Number(e.target.value), max) })}
              className="absolute inset-0 w-full appearance-none bg-transparent"
              style={{ pointerEvents: 'auto' }}
              aria-label="Minimum price"
            />
            <input
              type="range"
              min={priceBounds.min}
              max={priceBounds.max}
              value={max}
              onChange={(e) => commit({ ...draft, max_price: Math.max(Number(e.target.value), min) })}
              className="absolute inset-0 w-full appearance-none bg-transparent"
              style={{ pointerEvents: 'auto' }}
              aria-label="Maximum price"
            />
          </div>
          <div className="flex items-center justify-between mt-2" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
            <span>₹{min}</span>
            <span>₹{max}</span>
          </div>
        </div>
      );
    }

    if (group.type === 'swatch') {
      return (
        <div className="space-y-2 pt-1">
          {group.values.map((v) => {
            const selected = (draft[group.key] || []).includes(v.value);
            return (
              <label key={v.value} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => setListValue(group.key, v.value)}
                  className="w-3.5 h-3.5 accent-[#ff3f6c]"
                />
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ background: v.hex || '#e5e5e5', border: '1px solid rgba(0,0,0,0.12)' }}
                />
                <span className="flex-1" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{v.label}</span>
                <span style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>({v.count})</span>
              </label>
            );
          })}
        </div>
      );
    }

    // Sizes and brands both read best as a checkbox list with counts.
    return (
      <div className="space-y-2 pt-1">
        {group.values.map((v) => {
          const selected = (draft[group.key] || []).includes(v.value);
          return (
            <label key={v.value} className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => setListValue(group.key, v.value)}
                className="w-3.5 h-3.5 accent-[#ff3f6c]"
              />
              <span className="flex-1" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{v.label}</span>
              <span style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>({v.count})</span>
            </label>
          );
        })}
      </div>
    );
  };

  const stockToggle = (
    <label className="flex items-center gap-2.5 cursor-pointer py-3">
      <input
        type="checkbox"
        checked={Boolean(draft.in_stock)}
        onChange={(e) => commit({ ...draft, in_stock: e.target.checked })}
        className="w-3.5 h-3.5 accent-[#ff3f6c]"
      />
      <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Exclude out of stock</span>
    </label>
  );

  // ---- Desktop sidebar -------------------------------------------------- //
  if (inline) {
    return (
      <aside className="w-[250px] flex-shrink-0 py-5" style={{ borderRight: '1px solid var(--fx-line)' }}>
        <div className="flex items-center justify-between pb-3 pr-4" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <span className="fx-label">Filters</span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => { setDraft(EMPTY); onClear(); }}
              style={{ color: 'var(--fx-pink)', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em' }}
            >
              CLEAR ALL
            </button>
          )}
        </div>

        <div className="pr-4">
          <div className="py-4" style={{ borderBottom: '1px solid var(--fx-line)' }}>
            {stockToggle}
          </div>

          {groups.map((group) => (
            <div key={group.key} className="py-4" style={{ borderBottom: '1px solid var(--fx-line)' }}>
              <h4
                className="mb-2"
                style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fx-ink-2)' }}
              >
                {group.label}
              </h4>
              {renderValues(group)}
            </div>
          ))}
        </div>
      </aside>
    );
  }

  // ---- Mobile sheet ----------------------------------------------------- //
  const active = groups.find((g) => g.key === activeGroup) || groups[0];

  return (
    <div className="fixed inset-0 z-[240]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="absolute inset-x-0 bottom-0 bg-white flex flex-col" style={{ height: '85vh' }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <span className="fx-label">Filters</span>
          <button type="button" onClick={onClose} className="p-1 -mr-1" aria-label="Close filters">
            <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-32 flex-shrink-0 bg-[#f5f5f6] overflow-y-auto hide-scrollbar">
            {groups.map((group) => {
              const count = Array.isArray(draft[group.key]) ? draft[group.key].length : 0;
              const isActive = active?.key === group.key;
              return (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => setActiveGroup(group.key)}
                  className="w-full text-left px-3 py-3.5"
                  style={{
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 400,
                    background: isActive ? '#fff' : 'transparent',
                    borderLeft: isActive ? '3px solid var(--fx-pink)' : '3px solid transparent',
                    color: isActive ? 'var(--fx-ink)' : 'var(--fx-ink-2)',
                  }}
                >
                  {group.label}
                  {count > 0 && <span style={{ color: 'var(--fx-pink)' }}> ({count})</span>}
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            {active ? renderValues(active) : (
              <p className="py-6 text-center" style={{ fontSize: 14, color: 'var(--fx-ink-3)' }}>
                No filters available
              </p>
            )}
            <div style={{ borderTop: '1px solid var(--fx-line)' }} className="mt-3">
              {stockToggle}
            </div>
          </div>
        </div>

        <div className="flex" style={{ borderTop: '1px solid var(--fx-line)' }}>
          <button
            type="button"
            onClick={() => { setDraft(EMPTY); onClear(); onClose(); }}
            className="flex-1 py-3.5"
            style={{ fontSize: 14, fontWeight: 700, color: 'var(--fx-ink)' }}
          >
            CLEAR ALL
          </button>
          <button
            type="button"
            onClick={() => { onApply(draft); onClose(); }}
            className="flex-1 py-3.5 text-white"
            style={{ background: 'var(--fx-pink)', fontSize: 14, fontWeight: 700 }}
          >
            APPLY
          </button>
        </div>
      </div>
    </div>
  );
};

export default FashionFilterSheet;
